import { Star, BadgeCheck } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

type RuRegion = "sacramento" | "la" | "san-francisco" | "san-jose" | "san-diego" | "bay-area";

const TESTIMONIALS: Record<RuRegion, { name: string; city: string; quote: string }[]> = {
  sacramento: [
    { name: "Наталья К.", city: "Сакраменто, Калифорния", quote: "Отличный сервис! Команда приехала вовремя и оставила мой дом в идеальной чистоте. Рекомендую всем!" },
    { name: "Михаил Р.", city: "Розвилл, Калифорния", quote: "Глубокая уборка превзошла все мои ожидания. Очень профессионально и внимательно к деталям." },
    { name: "Анна Л.", city: "Элк Гров, Калифорния", quote: "С двумя детьми мне нужна надёжная команда. Uplevel Cleaning — просто замечательный сервис!" },
  ],
  la: [
    { name: "Ирина В.", city: "Лос-Анджелес, Калифорния", quote: "Превосходный сервис в LA! Цены прозрачные, команда профессиональная. Очень довольна результатом." },
    { name: "Дмитрий С.", city: "Санта-Моника, Калифорния", quote: "Мой кондоминиум у пляжа никогда не был таким чистым. Стоит каждого цента!" },
    { name: "Елена П.", city: "Пасадена, Калифорния", quote: "Бронирование онлайн за минуту, и дом блестит. Лучший выбор в Южной Калифорнии!" },
  ],
  "san-francisco": [
    { name: "Татьяна М.", city: "Сан-Франциско, Калифорния", quote: "В SF сложно найти время на уборку. Uplevel решил эту проблему — быстро, качественно и по честной цене." },
    { name: "Алексей Б.", city: "Дейли-Сити, Калифорния", quote: "Профессиональная уборка перед продажей квартиры. Покупатели были в восторге!" },
    { name: "Ольга Г.", city: "Бёрлингейм, Калифорния", quote: "Очень доволен сервисом. Команда внимательная, аккуратная и пунктуальная." },
  ],
  "san-jose": [
    { name: "Сергей Ч.", city: "Сан-Хосе, Калифорния", quote: "Как занятой разработчик в Silicon Valley, я ценю надёжный сервис. Uplevel всегда на высоте!" },
    { name: "Людмила А.", city: "Саннивейл, Калифорния", quote: "Уборка после ремонта была идеальной. Вся пыль исчезла, дом сияет чистотой." },
    { name: "Павел Н.", city: "Пало-Альто, Калифорния", quote: "Честные цены, онлайн-бронирование и отличный результат. Рекомендую всем в Bay Area!" },
  ],
  "san-diego": [
    { name: "Виктория К.", city: "Сан-Диего, Калифорния", quote: "Отличный сервис в Сан-Диего! Команда профессиональная, дом блестит после каждой уборки." },
    { name: "Роман Т.", city: "Карлсбад, Калифорния", quote: "Быстрое бронирование, честные цены и гарантия качества. Именно то, что нужно!" },
    { name: "Марина Ф.", city: "Энсинитас, Калифорния", quote: "Живу у океана — везде песок. Uplevel убирает всё до последней крупицы. Великолепно!" },
  ],
  "bay-area": [
    { name: "Вадим П.", city: "Уолнат-Крик, Калифорния", quote: "Сервис на высшем уровне в Contra Costa County. Команда профессиональная и пунктуальная." },
    { name: "Светлана Ю.", city: "Фремонт, Калифорния", quote: "Уборка после переезда была выполнена безупречно. Вернул залог в полном объёме!" },
    { name: "Игорь М.", city: "Дублин, Калифорния", quote: "Лучший выбор для Tri-Valley. Доступные цены и качество, которое превосходит ожидания." },
  ],
};

export const RussianTestimonials = ({ region }: { region: RuRegion }) => {
  const { ref, isVisible } = useScrollAnimation();
  const reviews = TESTIMONIALS[region] || TESTIMONIALS.sacramento;

  return (
    <section className="py-24 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-16 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Отзывы наших клиентов</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Реальные отзывы довольных клиентов в вашем районе.
          </p>
        </div>
<div className={cn("anim-fade-up", isVisible && "is-visible")}>
          <div className="md:hidden">
            <div className="flex overflow-x-auto pb-8 -mx-4 px-4 snap-x snap-mandatory hide-scrollbar gap-4">
              {reviews.map((review, i) => (
                <div key={i} className="bg-background p-6 rounded-2xl shadow-sm border flex flex-col gap-4 min-w-[85vw] snap-center shrink-0">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
                  </div>
                  <p className="text-muted-foreground italic leading-relaxed text-sm">"{review.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm">{review.name}</span>
                        <BadgeCheck className="h-4 w-4 text-primary" />
                      </div>
                      <p className="text-xs text-muted-foreground">{review.city}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, i) => (
              <div key={i} className="bg-background p-8 rounded-2xl border shadow-sm hover:shadow-lg transition-all" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-current" />)}
                </div>
                <p className="text-muted-foreground mb-6 italic leading-relaxed">"{review.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-sm">{review.name}</span>
                      <BadgeCheck className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-xs text-muted-foreground">{review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
